import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Plug, BookOpen, Zap } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: Plug,
    title: 'Integrate with your existing tools',
    desc: 'We connect to Gorgias, Zendesk, Intercom, Help Scout, or whatever you currently use. No platform changes required.',
  },
  {
    number: '02',
    icon: BookOpen,
    title: 'Build your retention playbook',
    desc: 'We map your offers, margins, and customer profile into a custom retention playbook our agents follow on every save attempt.',
  },
  {
    number: '03',
    icon: Zap,
    title: 'Save customers in real time',
    desc: 'Every cancellation request is handled within 2 hours by a trained retention agent following your playbook.',
  },
];

export default function HowItWorks() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start 0.8', 'end 0.2'],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="py-28 px-6 lg:px-10 bg-[#FAFAF7]"
    >
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A1628]/40 mb-4">
            How it works
          </p>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0A1628]">
            Simple to start, powerful by design
          </h2>
        </motion.div>

        <div ref={containerRef} className="relative">
          {/* Vertical track */}
          <div className="absolute left-[27px] top-8 bottom-8 w-px bg-[#0A1628]/10 hidden md:block" />
          {/* Animated fill */}
          <motion.div
            className="absolute left-[27px] top-8 w-px bg-[#00D4B8] origin-top hidden md:block"
            style={{ height: lineHeight }}
          />

          <div className="space-y-14">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.number}
                  data-testid={`step-${i + 1}`}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                  className="flex gap-8 md:gap-10 items-start"
                >
                  {/* Node */}
                  <div className="relative flex-shrink-0">
                    <div className="w-[54px] h-[54px] rounded-full bg-white border-2 border-[#00D4B8] flex items-center justify-center shadow-sm z-10 relative">
                      <Icon size={22} strokeWidth={1.5} className="text-[#00D4B8]" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="pt-2 pb-6">
                    <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#00D4B8] mb-2 block">
                      Step {step.number}
                    </span>
                    <h3 className="text-xl font-semibold text-[#0A1628] mb-3 leading-snug">
                      {step.title}
                    </h3>
                    <p className="text-base text-[#1A1A1A]/60 leading-relaxed max-w-lg">
                      {step.desc}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
